import React, { memo, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import "./styles.scss";

const StyledSelectField = ({
  onChange = () => {},
  value = "",
  label = "",
  optionsList = [],
  placeholder = "Select...",
  marginBottom = 0,
  errorMessage = "",
  isRequired = false,
  disabled = false,
  styles = {},
  onMenuScrollToBottom = () => {},
  isLoading = false,
  isSearchable = false,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (!menuOpen) return;

    const timer = setTimeout(() => {
      const menu = document.querySelector(".react-select__menu");
      const selected = document.querySelector(
        ".react-select__option--is-selected"
      );

      if (menu && selected) {
        menu.scrollTop = selected.offsetTop - 5; // scroll directly
      }
    }, 50); // wait for DOM to render

    return () => clearTimeout(timer);
  }, [menuOpen]);

  // Filter out any empty option in the optionsList
  const filteredOptionsList = optionsList;

  const getValue = useMemo(() => {
    if (value === "") {
      return null;
    }
    const val = filteredOptionsList.find(function (option) {
      return option.value === value;
    });
    return val;
  }, [filteredOptionsList, value]);

  // Custom filter function that prioritizes exact matches and sorts by relevance
  const customFilterOption = (option, inputValue) => {
    if (!inputValue) return true;

    const label = option.label?.toString().toLowerCase() || "";
    const searchText = inputValue.toLowerCase();

    // Return true if the label contains the search text
    return label.includes(searchText);
  };

  // Sort options based on search input
  const sortedOptions = useMemo(() => {
    if (!inputValue || !isSearchable) return filteredOptionsList;

    const searchText = inputValue.toLowerCase();

    // Filter and sort options by relevance
    return [...filteredOptionsList]
      .filter((option) => {
        const label = option.label?.toString().toLowerCase() || "";
        return label.includes(searchText);
      })
      .sort((a, b) => {
        const labelA = a.label?.toString().toLowerCase() || "";
        const labelB = b.label?.toString().toLowerCase() || "";

        // Priority 1: Exact match
        if (labelA === searchText) return -1;
        if (labelB === searchText) return 1;

        // Priority 2: Starts with search text
        const aStarts = labelA.startsWith(searchText);
        const bStarts = labelB.startsWith(searchText);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;

        // Priority 3: Contains search text earlier in the string
        const aIndex = labelA.indexOf(searchText);
        const bIndex = labelB.indexOf(searchText);
        if (aIndex !== bIndex) return aIndex - bIndex;

        // Priority 4: Alphabetical
        return labelA.localeCompare(labelB);
      });
  }, [filteredOptionsList, isSearchable, inputValue]);

  return (
    <div
      className="styled-select-field"
      style={{ padding: "0px", marginBottom: marginBottom, ...styles }}
    >
      {label !== "" && (
        <div className="label-styles">
          {label}
          {isRequired && <span className="required-staric-color">*</span>}
        </div>
      )}
      <Select
        options={sortedOptions}
        value={getValue}
        classNamePrefix={"singal-select"}
        onChange={onChange}
        isDisabled={disabled}
        onMenuOpen={() => setMenuOpen(true)}
        onMenuClose={() => {
          setMenuOpen(false);
          setInputValue("");
        }}
        placeholder={placeholder}
        onMenuScrollToBottom={onMenuScrollToBottom}
        isLoading={isLoading}
        isSearchable={isSearchable}
        inputValue={inputValue}
        onInputChange={(newValue, actionMeta) => {
          if (actionMeta.action === "input-change") {
            setInputValue(newValue);
          }
        }}
        filterOption={customFilterOption}
        menuPortalTarget={document.body}
        menuShouldScrollIntoView={false}
        menuPlacement="auto"
        styles={{
          option: (provided, state) => ({
            ...provided,
            color: state.isSelected ? "white" : "#8C94A3",
            maxHeight: "42px",
          }),
          menuList: (base) => ({
            ...base,
            maxHeight: "200px",
            zIndex: 90000,
          }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }), // make sure it's above modal
        }}
      />
      {errorMessage?.length ? (
        <div style={{ fontSize: "11px", fontWeight: 500, color: "red" }}>
          {errorMessage}
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default memo(StyledSelectField);
